import React, { useState, useEffect, forwardRef } from 'react'
import { Navbar, Button } from 'reactstrap'
import { useNavigate, useLocation } from 'react-router-dom'

import { ROUTES, HIDE_NAVBAR_PATHS } from '@common'
import { useAuth } from '@hooks'
import Sidebar from './Sidebar'

// Common styles
const navbarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: '#f8f9fa',
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
    zIndex: 1000,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
}

const sidebarButtonStyle = {
    backgroundColor: 'transparent',
    border: '1px solid #6c757d',
    borderRadius: '6px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
}

const signInButtonStyle = {
    backgroundColor: 'transparent',
    border: '1px solid #28a745',
    color: '#28a745',
    padding: '6px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
}

// Sidebar button component
const SidebarButton = ({ onClick }) => (
    <button onClick={onClick} style={sidebarButtonStyle}>
        <i className="bx bx-menu text-dark" style={{ fontSize: '24px' }} />
    </button>
)

// Sign in button component
const SignInButton = ({ onClick, text = 'Sign In' }) => (
    <div>
        <Button color="success" size="sm" outline onClick={onClick} style={signInButtonStyle}>
            {text}
        </Button>
        <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>For 2% Rebate</p>
    </div>
)

const NavBar = forwardRef((props, ref) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { isAuthenticated, memberData } = useAuth()

    const [isVisible, setIsVisible] = useState(true)
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const hideThreshold = 50

    // Check if the current page should hide the NavBar
    const shouldHideNavBar = HIDE_NAVBAR_PATHS.some(path => location.pathname.includes(path))
    // Check if the current page is TablePage
    const isTablePage = location.pathname === ROUTES.Table.root

    useEffect(() => {
        const handleScroll = () => {
            setIsVisible(window.scrollY <= hideThreshold)
        }

        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [hideThreshold])

    const handleSidebarToggle = () => {
        setIsSidebarOpen(!isSidebarOpen)
    }

    const handleLogoClick = () => {
        navigate(ROUTES.root)
    }

    const handleSignIn = () => {
        navigate(ROUTES.Membership.auth)
    }

    // Return null if NavBar should be hidden
    if (shouldHideNavBar) {
        return null
    }

    // Get dynamic navbar style
    const dynamicNavbarStyle = {
        ...navbarStyle,
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        opacity: isVisible ? 1 : 0,
    }

    // Render left side content based on page type
    const renderLeftContent = () => {
        if (isTablePage) {
            return (
                <div className="d-flex align-items-center">
                    <SidebarButton onClick={handleSidebarToggle} />
                    {isAuthenticated ?
                        <div className="d-flex flex-column" style={{ marginLeft: '16px' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', margin: 0 }}>
                                Hi, {memberData.name || 'User'}
                            </div>
                            <div style={{ fontSize: '12.5px', color: '#666', margin: 0 }}>
                                Available Points:{' '}
                                {memberData.credit?.totalAvailableCents ? memberData.credit.totalAvailableCents : '0.00'}
                            </div>
                        </div>
                    :   <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginLeft: '10px' }}>Hi, Guest</div>}
                </div>
            )
        }

        return (
            <div className="d-flex align-items-center gap-3">
                <SidebarButton onClick={handleSidebarToggle} />
                {isAuthenticated && memberData.name ?
                    <div className="d-flex flex-column">
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', margin: 0 }} onClick={handleLogoClick}>
                            Local Chapter
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                            Signed in as <span style={{ fontWeight: 'bold' }}>{memberData.name}</span>
                        </div>
                    </div>
                :   <div
                        className="d-flex flex-column"
                        style={{ fontSize: '18px', fontWeight: '600', color: '#333', cursor: 'pointer' }}
                        onClick={handleLogoClick}
                    >
                        <div style={{ lineHeight: '1.2' }}>Welcome to</div>
                        <div style={{ lineHeight: '1.2' }}>Local Chapter</div>
                    </div>
                }
            </div>
        )
    }

    // Render right side content
    const renderRightContent = () => {
        if (!isAuthenticated) {
            return (
                <div className="d-flex align-items-center">
                    <SignInButton onClick={handleSignIn} text={'Sign In'} />
                </div>
            )
        }
        return null
    }

    return (
        <>
            <Navbar ref={ref} light expand="md" className="px-2 py-3" style={dynamicNavbarStyle}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    {renderLeftContent()}
                    {renderRightContent()}
                </div>
            </Navbar>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        </>
    )
})

NavBar.displayName = 'NavBar'

export default NavBar
